import React, { useEffect, useMemo, useState } from "react";
import { MDBContainer, MDBBtn, MDBIcon } from "mdbreact";
import { get } from "api/index";
import { getCatalogMemberStatus } from "api/stripe/checkoutSession";
import RegisterCatalogModal from "../../components/PricingCatalogs/RegisterCatalogModal";

// Public daily market feed: the published crawl history (BLS PPI indexes for
// war/geopolitics-sensitive construction inputs, plus BLS construction wages
// in $/hour) from CurLight - auto-published once confirmed, or from its
// approval console. Same viewing contract as /pricing-catalogues: anyone can
// browse sources, dates and references; the numbers are locked until the
// viewer is a member ($19 registration or a logged-in account).
const getMarketFeed = () => get("/data/v2/market-feed?$limit=365", true);

const CATEGORY_LABELS = {
  fuel_energy: "Fuel & Energy",
  steel: "Steel",
  lumber: "Lumber",
  cement_concrete: "Cement & Concrete",
  construction_materials: "Construction Materials",
  construction_labour: "Construction Labour",
  fuel_price: "Fuel Price",
};

// Categories of the component sources come in families (raw_*, mfg_ratio_*,
// margin_*, construction_split_*, equipment_rate_*).
function categoryLabel(c) {
  if (CATEGORY_LABELS[c]) return CATEGORY_LABELS[c];
  if (/^raw_/.test(c)) return "Raw Materials";
  if (/^mfg_ratio_/.test(c)) return "Manufacturing";
  if (/^margin_(wholesale|retail)/.test(c)) return "Store Issue";
  if (/^margin_contractor/.test(c)) return "Contractor Fees";
  if (/^construction_split_/.test(c)) return "Construction Cost Split";
  if (/^equipment_rate_/.test(c)) return "Equipment";
  return c;
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

// Period codes: 2026-M09 (BLS month), 2025-A (a year: USGS, Census, BEA),
// 2026-10-19 (EIA week), 2027-03-V (Caltrans rates valid to).
function periodName(p) {
  let m;
  if ((m = /^(\d{4})-M(\d{2})$/.exec(p || ""))) return `${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
  if ((m = /^(\d{4})-A$/.exec(p || ""))) return `Year ${m[1]}`;
  if ((m = /^(\d{4})-(\d{2})-V$/.exec(p || ""))) return `Rates valid to ${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(p || "")) return `Week of ${p}`;
  return p || "Earlier";
}

// The date a period ends, so the groups sort newest first whatever their
// kind (a string sort put "2027-03-V" above every month).
function periodEnd(p) {
  let m;
  if ((m = /^(\d{4})-M(\d{2})$/.exec(p || ""))) return Date.UTC(+m[1], +m[2], 0);
  if ((m = /^(\d{4})-A$/.exec(p || ""))) return Date.UTC(+m[1], 11, 31);
  if ((m = /^(\d{4})-(\d{2})-V$/.exec(p || ""))) return Date.UTC(+m[1], +m[2], 0);
  if ((m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(p || ""))) return Date.UTC(+m[1], +m[2] - 1, +m[3]);
  return 0;
}

// A level is a price ($41.69 /h); an index only says how a price moved
// (1982 = 100), so it is labelled as one.
function ValueCell({ row, locked }) {
  if (locked || row.indexValue === null || row.indexValue === undefined) return "🔒";
  if (row.valueKind === "level" && /^usd_per_hour/.test(row.unit || ""))
    return `$${Number(row.indexValue).toFixed(2)} /h`;
  if (row.unit === "usd_per_gallon") return `$${Number(row.indexValue).toFixed(3)} /gal`;
  if (row.unit === "usd_per_metric_ton") return `$${Number(row.indexValue).toFixed(2)} /t`;
  if (/^pct_/.test(row.unit || "")) return `${Number(row.indexValue).toFixed(2)} %`;
  if (row.valueKind === "level") return `${row.indexValue} ${row.unit || ""}`;
  return (
    <>
      {row.indexValue} <span className="text-muted small">index</span>
    </>
  );
}

function DeltaCell({ value, locked }) {
  if (locked) return <span title="Members only">🔒</span>;
  if (value === null || value === undefined)
    return <span className="text-muted">–</span>;
  const up = value > 0;
  const color = up ? "#d9534f" : value < 0 ? "#28a745" : "#6c757d";
  return (
    <span style={{ color, fontWeight: 600 }}>
      {up ? "▲" : value < 0 ? "▼" : ""} {value.toFixed(2)}%
    </span>
  );
}

export default function MarketFeed() {
  const [rows, setRows] = useState(null);
  const [masked, setMasked] = useState(true);
  const [isMember, setIsMember] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getMarketFeed().catch(() => null),
      getCatalogMemberStatus().catch(() => null),
    ]).then(([feed, status]) => {
      if (cancelled) return;
      if (!feed || !Array.isArray(feed.rows)) {
        setError("The market feed is unavailable right now. Please try again later.");
        setRows([]);
        return;
      }
      setRows(feed.rows);
      setMasked(Boolean(feed.masked));
      setIsMember(Boolean(status?.canExport));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // One row per (source, period), grouped by the month the data is FOR. The
  // feed is newest-first, so the first row met is the current value; an
  // earlier one for the same pair makes it a revision.
  const periods = useMemo(() => {
    const byPeriod = {};
    const seen = {};
    (rows || []).forEach((r) => {
      const k = `${r.sourceId}|${r.period}`;
      if (seen[k]) {
        seen[k].revised = true;
        return;
      }
      seen[k] = { ...r };
      (byPeriod[r.period] = byPeriod[r.period] || []).push(seen[k]);
    });
    return Object.keys(byPeriod)
      .sort((x, y) => periodEnd(y) - periodEnd(x))
      .map((period) => ({ period, items: byPeriod[period] }));
  }, [rows]);

  const locked = masked && !isMember;

  return (
    <MDBContainer fluid className="py-4" style={{ maxWidth: 1100 }}>
      <div className="d-flex align-items-start flex-wrap">
        <div style={{ flex: 1, minWidth: 260 }}>
          <h2 className="mb-1">Daily Market Price Feed</h2>
          <p className="text-muted mb-2">
            How war and geopolitics move construction input prices — diesel,
            steel, lumber, cement and materials — tracked daily from official
            sources, reviewed before publication. Every figure links to its
            source so you can investigate it yourself.
          </p>
        </div>
        {locked ? (
          <MDBBtn color="primary" onClick={() => setRegisterOpen(true)}>
            Unlock prices — $19
          </MDBBtn>
        ) : null}
      </div>

      {locked ? (
        <div
          className="p-3 mb-3"
          style={{ background: "#f6f8fb", border: "1px solid #e3e9f2", borderRadius: 8 }}
        >
          <MDBIcon icon="lock" className="mr-2" />
          Sources, dates and references are free to browse. Index values and
          daily changes are visible to members —{" "}
          <a href="#unlock" onClick={(e) => { e.preventDefault(); setRegisterOpen(true); }}>
            register for a one-time $19
          </a>{" "}
          or <a href="/authorization">log in</a>.
        </div>
      ) : null}

      {error ? <div className="alert alert-warning">{error}</div> : null}

      {rows === null ? (
        <p className="text-muted">Loading feed…</p>
      ) : periods.length === 0 && !error ? (
        <p className="text-muted">
          No published prices yet — the daily crawl reads its sources at 03:00
          UTC and a figure appears here once it is confirmed.
        </p>
      ) : (
        periods.map(({ period, items }) => (
          <div key={period} className="mb-4">
            <h5 className="mb-2" style={{ fontWeight: 600 }}>
              {periodName(period)}
            </h5>
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0" style={{ background: "#fff" }}>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Category</th>
                    <th className="text-right">Value</th>
                    <th className="text-right">Change on prior month</th>
                    <th>Published</th>
                    <th>References</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const ref = (r.sourceRefs && r.sourceRefs[0]) || {};
                    const extraRefs =
                      (r.eventTags || [])
                        .flatMap((t) => t.referenceUrls || [])
                        .filter((u) => u && u !== ref.evidenceUrl);
                    return (
                      <tr key={r._id}>
                        <td>{ref.name || r.sourceId}</td>
                        <td>{categoryLabel(r.category)}</td>
                        <td className="text-right">
                          <ValueCell row={r} locked={locked} />
                        </td>
                        <td className="text-right">
                          <DeltaCell value={r.pctDelta} locked={locked || r.pctDelta === null} />
                          {r.largeMove ? (
                            <span className="text-muted small ml-1">large move</span>
                          ) : null}
                        </td>
                        <td>
                          {String(r.publishedAt || "").slice(0, 10)}
                          {r.revised || r.revises ? (
                            <span className="text-muted small ml-1">revised</span>
                          ) : null}
                        </td>
                        <td>
                          {ref.evidenceUrl ? (
                            <a href={ref.evidenceUrl} target="_blank" rel="noopener noreferrer">
                              {ref.seriesId || "source"}
                            </a>
                          ) : null}
                          {extraRefs.map((u, i) => (
                            <span key={u}>
                              {" · "}
                              <a href={u} target="_blank" rel="noopener noreferrer">
                                ref{i + 1}
                              </a>
                            </span>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      <RegisterCatalogModal open={registerOpen} onClose={() => setRegisterOpen(false)} />
    </MDBContainer>
  );
}
